import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { roleAccessService } from '../../services/roleAccessService';
import { isAdmin } from '../../utils/permissions';

// Modules whose underlying data is sensitive (e.g. People — salaries and pay
// cost). Granting one requires an explicit confirmation, and it is deliberately
// excluded from the bulk "Select all" so access can never be handed out en
// masse. This mirrors the sidebar, where sensitive modules are not unlocked by
// the admin blanket.
//
// The flag now comes from md_modules.is_sensitive (the same one seedAdmin reads
// so it never bulk-grants People). This local set is only the fallback for a
// database that predates 2026_md_modules_sensitive.sql.
const FALLBACK_SENSITIVE = new Set(['MOD_8_PEOPLE']);

const Checklist = ({ title, items, selected, onToggle, onBulk, disabled, search, setSearch, emptyLabel }) => {
  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((i) => i.code.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q))
    : items;
  const filteredCodes = filtered.map((i) => i.code);
  return (
    <div className="flex flex-col h-full min-h-0 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 shrink-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</span>
        <span className="text-[10px] text-slate-400">{selected.size} selected</span>
      </div>
      <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 shrink-0 flex items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="flex-1 min-w-0 px-2 py-1.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 outline-none focus:border-blue-400"
        />
        <button type="button" disabled={disabled || filteredCodes.length === 0}
          onClick={() => onBulk(filteredCodes, true)}
          className="shrink-0 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline">Select all</button>
        <button type="button" disabled={disabled || filteredCodes.length === 0}
          onClick={() => onBulk(filteredCodes, false)}
          className="shrink-0 text-xs text-slate-500 dark:text-slate-400 hover:underline disabled:opacity-40 disabled:no-underline">Clear all</button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="p-4 text-center text-xs text-slate-400">{emptyLabel}</p>
        ) : filtered.map((i) => (
          <label key={i.code} className="flex items-start gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={selected.has(i.code)} disabled={disabled} onChange={() => onToggle(i.code)} />
            <span className="min-w-0">
              <span className="font-medium text-slate-700 dark:text-slate-200">{i.code}</span>
              {i.description && <span className="text-slate-400"> — {i.description}</span>}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

const RoleAccessModal = ({ onClose }) => {
  const admin = isAdmin();
  const [roles, setRoles] = useState([]);
  const [urid, setUrid] = useState('');
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [modules, setModules] = useState([]);
  const [ccAccess, setCcAccess] = useState(new Set());
  const [glAccess, setGlAccess] = useState(new Set());
  const [moduleAccess, setModuleAccess] = useState(new Set());
  // Reporting sections. MOD_10_REPORT is one module holding many sections
  // (Revenue Management, HR, Finance, Sales, ...), and the section is the unit
  // of access: a payroll analyst and a category manager both "have reporting"
  // and must not see each other's numbers.
  const [reportSections, setReportSections] = useState([]);
  const [reportAccess, setReportAccess] = useState(new Set());
  const [ccSearch, setCcSearch] = useState('');
  const [glSearch, setGlSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingModule, setPendingModule] = useState(null); // sensitive module awaiting confirmation

  // Sensitivity is a property of the module (md_modules.is_sensitive), so this
  // screen, the sidebar and seedAdmin all answer the question the same way.
  const isSensitive = useCallback(
    (code) => modules.find((m) => m.code === code)?.is_sensitive ?? FALLBACK_SENSITIVE.has(code),
    [modules],
  );

  const roleName = roles.find((r) => String(r.id) === String(urid))?.name || 'this role';

  useEffect(() => {
    api.get('/users/all-roles').then((r) => setRoles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    roleAccessService.getCostCenters().then((r) => { if (r.success) setCostCenters(r.data); }).catch(() => {});
    roleAccessService.getGlAccounts().then((r) => { if (r.success) setGlAccounts(r.data); }).catch(() => {});
    roleAccessService.getModules().then((r) => { if (r.success) setModules(r.data); }).catch(() => {});
    // Empty on a tenant that has not run 2026_reporting.sql yet — the block
    // below simply does not render, rather than the screen breaking.
    roleAccessService.getReportSections().then((r) => { if (r.success) setReportSections(r.data); }).catch(() => {});
  }, []);

  const loadAccess = useCallback(async (id) => {
    if (!id) {
      setCcAccess(new Set()); setGlAccess(new Set());
      setModuleAccess(new Set()); setReportAccess(new Set());
      return;
    }
    setLoading(true); setError('');
    try {
      const r = await roleAccessService.getRoleAccess(id);
      if (r.success) {
        setCcAccess(new Set(r.data.cost_centers));
        setGlAccess(new Set(r.data.gl_accounts));
        setModuleAccess(new Set(r.data.modules || []));
        setReportAccess(new Set(r.data.report_sections || []));
      }
    } catch {
      setError('Failed to load role access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAccess(urid); }, [urid, loadAccess]);

  // Per-dimension config so toggle/bulk handle cost-centres, GL accounts and modules uniformly.
  const accessConfig = {
    cc: { set: ccAccess, setSet: setCcAccess, add: roleAccessService.addCostCenter, remove: roleAccessService.removeCostCenter, bulk: roleAccessService.bulkCostCenters },
    gl: { set: glAccess, setSet: setGlAccess, add: roleAccessService.addGlAccount, remove: roleAccessService.removeGlAccount, bulk: roleAccessService.bulkGlAccounts },
    mod: { set: moduleAccess, setSet: setModuleAccess, add: roleAccessService.addModule, remove: roleAccessService.removeModule, bulk: roleAccessService.bulkModules },
    rep: { set: reportAccess, setSet: setReportAccess, add: roleAccessService.addReportSection, remove: roleAccessService.removeReportSection, bulk: roleAccessService.bulkReportSections },
  };

  const toggle = (kind, code) => {
    if (!admin || !urid) return;
    const { set, setSet, add, remove } = accessConfig[kind];
    const has = set.has(code);
    setSet((prev) => { const n = new Set(prev); has ? n.delete(code) : n.add(code); return n; });
    (has ? remove : add)(Number(urid), code).catch(() => {
      setError('Failed to update access — change reverted.');
      setSet((prev) => { const n = new Set(prev); has ? n.add(code) : n.delete(code); return n; });
    });
  };

  const bulk = (kind, codes, select) => {
    if (!admin || !urid || codes.length === 0) return;
    const { setSet, bulk: bulkCall } = accessConfig[kind];
    setSet((prev) => { const n = new Set(prev); codes.forEach((c) => (select ? n.add(c) : n.delete(c))); return n; });
    bulkCall(Number(urid), codes, select ? 'add' : 'remove').catch(() => {
      setError('Bulk update failed — reloading current access.');
      loadAccess(urid);
    });
  };

  // Enabling a sensitive module is deferred until the admin confirms; disabling
  // and non-sensitive modules toggle immediately.
  const handleModuleToggle = (code) => {
    if (!admin || !urid) return;
    const enabling = !moduleAccess.has(code);
    if (enabling && isSensitive(code)) {
      setPendingModule(code);
      return;
    }
    toggle('mod', code);
  };

  const confirmSensitive = () => {
    const code = pendingModule;
    setPendingModule(null);
    if (code) toggle('mod', code);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-800 w-full max-w-4xl h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Role Data Access</h2>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-lg leading-none">✕</button>
        </div>

        <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0 flex items-center gap-3 flex-wrap">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Role</label>
          <select
            value={urid}
            onChange={(e) => setUrid(e.target.value)}
            className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">Select a role…</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {loading && <span className="text-xs text-slate-400">Loading…</span>}
          {!admin && <span className="text-xs text-amber-600">Read-only — an administrative role is required to change access.</span>}
        </div>

        {error && <div className="px-6 py-2 text-xs text-red-500 shrink-0">{error}</div>}

        {/* ONE SCROLL REGION FOR THE WHOLE BODY.
            These blocks used to be shrink-0 above a pair of flex-1 checklists,
            which worked while Reporting held a single section. It now holds one
            per operational module, so the fixed part grew past the height of the
            dialog and squeezed the cost-centre and GL lists into a few pixels.
            Everything scrolls together instead, and the checklists get a
            definite height so their own internal scrolling still works rather
            than nesting a scrollbar inside a scrollbar. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
        {urid && (
          <div>
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
              <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Module Access</span>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-400">{moduleAccess.size} selected</span>
                  {/* Sensitive modules are excluded from bulk select-all — they must be granted one at a time, with confirmation. */}
                  <button type="button" disabled={!admin} onClick={() => bulk('mod', modules.map((m) => m.code).filter((c) => !isSensitive(c)), true)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40">Select all</button>
                  <button type="button" disabled={!admin} onClick={() => bulk('mod', modules.map((m) => m.code), false)}
                    className="text-xs text-slate-500 dark:text-slate-400 hover:underline disabled:opacity-40">Clear all</button>
                </div>
              </div>
              <div className="p-3 flex flex-wrap gap-x-5 gap-y-2">
                {modules.map((m) => {
                  const sensitive = isSensitive(m.code);
                  return (
                    <label key={m.code} title={m.description}
                      className={`flex items-center gap-2 text-xs cursor-pointer ${sensitive ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-slate-700 dark:text-slate-200'}`}>
                      <input type="checkbox" checked={moduleAccess.has(m.code)} disabled={!admin} onChange={() => handleModuleToggle(m.code)} />
                      {m.name}
                      {sensitive && (
                        <span title="Sensitive — access must be granted explicitly, even for admins"
                          className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                          🔒 Sensitive
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Reporting sections — now OVERRIDES, not the mechanism.
                A section follows the module it reports on (report_sections.
                module_code), so ticking Distribution above is what opens the
                Distribution reports. These boxes exist for the exception: a
                role that needs one section without the module behind it.
                Said plainly on screen, because a checklist that mostly does
                nothing is worse than no checklist at all. */}
            {reportSections.length > 0 && (
              <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
                <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Reporting Sections <span className="normal-case font-normal text-slate-400">— extra grants only</span>
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-400">{reportAccess.size} of {reportSections.length}</span>
                    {/* Sensitive sections are excluded from select-all for the
                        same reason sensitive modules are: they are never
                        granted in bulk, and the server would refuse anyway. */}
                    <button type="button" disabled={!admin} onClick={() => bulk('rep', reportSections.filter((s) => !s.is_sensitive).map((s) => s.code), true)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40">Select all</button>
                    <button type="button" disabled={!admin} onClick={() => bulk('rep', reportSections.map((s) => s.code), false)}
                      className="text-xs text-slate-500 dark:text-slate-400 hover:underline disabled:opacity-40">Clear all</button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="m-0 mb-2 text-[11px] text-slate-500 dark:text-slate-400">
                    A reporting section normally opens on its own, because this role holds the module it reports on —
                    tick Distribution above and the Distribution reports come with it. <strong>Leave these unticked
                    unless you want an exception</strong>: a section granted here is readable <em>without</em> the
                    module behind it. The role still needs the Reporting module above to reach the menu at all.
                  </p>
                  {/* The one grant this checklist cannot make. Stated where
                      someone would otherwise tick the box and wonder why
                      nothing happened. */}
                  <p className="m-0 mb-2 text-[11px] text-amber-600 dark:text-amber-400">
                    People &amp; Workforce is the exception to the exception: it follows the sensitive People module and
                    cannot be granted from here, by an administrator or anyone else.
                  </p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {reportSections.map((s) => {
                      // No override can grant a sensitive section — the server
                      // checks the module before it looks at this list at all.
                      const locked = !!s.is_sensitive;
                      // Already covered by the module grant, so ticking it
                      // changes nothing. Said, rather than left to be discovered.
                      const covered = !locked && !!s.module_code && moduleAccess.has(s.module_code);
                      return (
                        <label key={s.code} title={s.description}
                          className={`flex items-center gap-2 text-xs ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${locked ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}>
                          <input type="checkbox" checked={reportAccess.has(s.code)} disabled={!admin || locked}
                                 onChange={() => toggle('rep', s.code)} />
                          {s.name}
                          <span className="text-[10px] text-slate-400">{s.report_count}</span>
                          {locked && (
                            <span title="Follows the sensitive People module — grant the module itself above"
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                              🔒 Module only
                            </span>
                          )}
                          {covered && (
                            <span title={`Already granted by the ${s.module_name} module`}
                              className="text-[9px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                              via module
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {!moduleAccess.has('MOD_10_REPORT') && reportAccess.size > 0 && (
                    <p className="m-0 mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                      These grants have no effect until <strong>Reporting</strong> is ticked in Module Access above —
                      without it the role never sees the menu entry.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-col md:flex-row gap-4">
          {!urid ? (
            <div className="flex-1 h-40 flex items-center justify-center text-sm text-slate-400">
              Select a role to manage which cost centres and GL accounts it can access.
            </div>
          ) : (
            <>
              {/* A definite height, not flex-1: these sit inside the scroll
                  region above, where "fill the remaining space" has no meaning
                  and collapses them to nothing. */}
              <div className="flex-1 min-w-0 h-[26rem]">
                <Checklist
                  title="Cost Centre Access" items={costCenters} selected={ccAccess}
                  onToggle={(code) => toggle('cc', code)} onBulk={(codes, sel) => bulk('cc', codes, sel)} disabled={!admin}
                  search={ccSearch} setSearch={setCcSearch} emptyLabel="No cost centres found."
                />
              </div>
              <div className="flex-1 min-w-0 h-[26rem]">
                <Checklist
                  title="GL Account Access" items={glAccounts} selected={glAccess}
                  onToggle={(code) => toggle('gl', code)} onBulk={(codes, sel) => bulk('gl', codes, sel)} disabled={!admin}
                  search={glSearch} setSearch={setGlSearch} emptyLabel="No GL accounts found."
                />
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      {/* Sensitive-access confirmation — shown before granting a module like People. */}
      {pendingModule && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setPendingModule(null); }}
        >
          <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-start gap-3 px-6 py-5 border-b border-slate-200 dark:border-slate-700">
              <span className="text-2xl leading-none">🔒</span>
              <div>
                <h3 className="m-0 text-base font-semibold text-slate-900 dark:text-slate-100">Grant sensitive access?</h3>
                <p className="m-0 mt-0.5 text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  {modules.find((m) => m.code === pendingModule)?.name || pendingModule}
                </p>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="m-0 text-sm text-slate-600 dark:text-slate-300">
                These are <span className="font-semibold text-slate-800 dark:text-slate-100">sensitive People costs</span> —
                salaries, pay cost and headcount. Are you sure you want to give this access to{' '}
                <span className="font-semibold text-slate-800 dark:text-slate-100">{roleName}</span>?
              </p>
              <p className="m-0 mt-2 text-xs text-slate-400">
                Access is not granted by administrator rights alone — you are explicitly authorising it here.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setPendingModule(null)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSensitive}
                className="px-4 py-2 text-sm font-semibold rounded-md text-white bg-amber-600 hover:bg-amber-700 transition-colors"
              >
                Yes, grant access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleAccessModal;
